import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Components from "../../../components";
import {
  useCreateUserMutation,
  useGetUserQuery,
  useUpdateUserMutation,
  useGetClientAppsQuery,
  useGetUserGroupsQuery,
  useGetLanguagesQuery,
  useGetLayoutsQuery,
  useGetRolesQuery
} from "../services";
import { mergeObjects } from "../../../shared/helper";
import { FormContainer } from "../Shared";
import { userFormSchema, userEditFormSchema } from "../validations";
import { useForm, useStaticDropDownSearch } from "../../../hooks";
import { SvgIcon } from "../../Shared";
import { userPlaceholder } from "../../../assets";
import { parseServerError } from "../../../shared/helper";
import { FMDPStorage } from "../../../shared/helper";
import Countrycode from "../../../shared/countryCode.json";
import { getLocalizedString, storePreferredLanguage } from "../../../shared/translation";
import { useDropDownSearch } from "../../../hooks";

export const UserForm = ({ idToEdit = null, onClose }) => {
  const dropdownParams = { per_page: 1000, order_by: "name", application_variant_id: 1 };
  const [initialValues, setInitialValues] = useState({
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    external_driver_rfid: "",
    // driving_licence: "",
    // driving_licence_validity: "",
    // driver_description: "",
    roles: [],
    image: "",
    is_enabled: true,
    country_code: "",
    phone_no: "",
    user_group_ids: [],
    locale_id: "",
    layout_id: "",
  });

  const formik = useFormik({
    initialValues,
    enableReinitialize: true,
    validationSchema: idToEdit ? userEditFormSchema : userFormSchema,
    onSubmit: (values) => handleFormSubmit(values),
  });

  // Get the langauges list using Dropdown Search
  const {
    data: languages,
    setSearchKey: setLanguagesSearch,
    isSuccess: isLanguagesSuccess,
  } = useDropDownSearch({
    useData: useGetLanguagesQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.locale_id,
    additionalParams: { ...dropdownParams },
  });

  // Get roles list
  const {
    data: rolesList,
    setSearchKey: setRolesSearch,
    isSuccess: isRolesSuccess,
  } = useDropDownSearch({
    useData: useGetRolesQuery,
  });

  // Get the layouts list
  const { data: layouts, isSuccess: isLayoutsSuccess } = useDropDownSearch({
    useData: useGetLayoutsQuery,
    simpleSearchKey: "name_cont",
    selectedIds: formik.values.layout_id,
  });

  const {
    create,
    update,
    error: updateError,
    updateSuccess,
    resetUpdate,
  } = useForm({
    createMutation: useCreateUserMutation,
    updateMutation: useUpdateUserMutation,
    closeForm: onClose,
    onSuccess: () => {
      const currentUser = FMDPStorage.get("current-user");
      if (currentUser.id == idToEdit) {
        //window.location.reload();
      }
    },
    setError: formik.setErrors,
  });

  const { data } = useGetUserQuery({ id: idToEdit }, { skip: !idToEdit });

  const { data: clientApps = { data: [] }, isSuccess: isClientAppsSuccess } = useGetClientAppsQuery();

  // Get the country code list using Dropdown Search for static data
  const { data: countryCode, setSearchKey: setCountryCodeSearchKey } = useStaticDropDownSearch({
    dataSet: Countrycode,
    selectedIds: formik.values.country_code,
  });

  const { data: userGroups, isSuccess: isuserGroupsSuccess } =
    useGetUserGroupsQuery(dropdownParams);

  const handleRoleSelection = (roleId, clientAppId) => {
    const tempFormData = {
      ...formik.values,
      roles: [...formik.values.roles], // Create a shallow copy of roles array
    };

    const existingIndex = tempFormData.roles.findIndex(
      ([cAppId, rId]) => cAppId === clientAppId && rId === roleId
    );

    if (existingIndex >= 0) {
      tempFormData.roles.splice(existingIndex, 1);
    } else {
      tempFormData.roles.push([clientAppId, roleId]);
    }

    formik.setValues(tempFormData);
  };

  // Set form data in case of edit
  useEffect(() => {
    if (idToEdit && data) {
      setInitialValues(mergeObjects(initialValues, data));
    }
  }, [data]);

  useEffect(() => {
    if (updateError) {
      formik.setErrors(parseServerError(updateError));
    }
  }, [updateError]);

  // To check whether the roles are equal or not
  function areRolesEqual(role1 = [], role2 = []) {
    if (role1.length !== role2.length) return false;

    const toKey = (pair) => pair.join(",");
    const setRole1 = new Set(role1.map(toKey));
    const setRole2 = new Set(role2.map(toKey));

    if (setRole1.size !== setRole2.size) return false;
    for (let key of setRole1) {
      if (!setRole2.has(key)) return false;
    }
    return true;
  }

  // On Update Success - Handle reload logic
  useEffect(() => {
    if (!updateSuccess) return;

    const currentUser = JSON?.parse(localStorage?.getItem("current-user"));
    const { id } = currentUser || {};
    const isCurrentUser = idToEdit === id;

    // Only reload if current user is being edited
    if (!isCurrentUser) return;

    // Check if roles or enabled status changed for current user
    const prevRoles = data?.roles;
    const updatedRole = formik.getFieldProps("roles").value;
    const rolesChanged = areRolesEqual(prevRoles, updatedRole) === false;
    const enabledChanged = data?.is_enabled !== formik.getFieldProps("is_enabled").value;

    // Check if language changed
    const prevLocaleId = data?.locale_id;
    const currentLocaleId = formik?.values.locale_id;
    const languageChanged = prevLocaleId !== currentLocaleId;

    // If roles or status changed, clear storage and reload
    if (rolesChanged || enabledChanged) {
      FMDPStorage.clear();
      window.location.reload();
      return;
    }

    // If language changed, update storage and reload
    if (languageChanged) {
      const selectedLanguage = languages?.data.find((lan) => lan?.id == currentLocaleId);
      if (selectedLanguage) {
        storePreferredLanguage(currentUser, selectedLanguage);
      }
      window.location.reload();
    }
  }, [updateSuccess, data]);

  const handleFormSubmit = (formData) => {
    let fObj = new FormData();

    Object.keys(formData).forEach((key) => {
      if (key === "image" && formData.image == null) {
        fObj.append("remove_image", true);
      } else if (!(key === "image" && formData.image === "") && formData[key] != null) {
        if (!idToEdit || (idToEdit && key !== "password")) {
          const value = formData[key];

          if (Array.isArray(value)) {
            // Check if array contains objects (for roles or similar)
            if (typeof value[0] === "object") {
              fObj.append(key, JSON.stringify(value));
            } else {
              // Assume array of strings/numbers
              const arrayOfIntegers = value.map((v) => parseInt(v));
              fObj.append(key, JSON.stringify(arrayOfIntegers));
            }
          } else {
            fObj.append(key, value);
          }
        }
      }
    });

    if (idToEdit) {
      update({ formData: fObj, id: idToEdit });
    } else {
      create({ formData: fObj });
    }
  };

  // Function to check if the user group is deletable
  const isUserGroupDeletable = ({ created_by }) => {
    if (created_by === "System") {
      return false;
    }
    return true;
  };

  // To show message when hovered on system group
  const handleUserGroupSelection = ({ created_by }) => {
    if (created_by === "System") {
      return `System groups can't be removed or selected)`;
    }
    return "";
  };

  // Set default user group as System Default if its create form and system default group is present
  useEffect(() => {
    if (isuserGroupsSuccess && userGroups?.data?.length > 0 && idToEdit === null) {
      userGroups?.data.map((group) => {
        if (group?.created_by === "System") {
          formik.setFieldValue("user_group_ids", [group.id]);
        }
      });
    }
  }, [isuserGroupsSuccess]);

  return (
    <FormContainer
      resourceName={getLocalizedString("user", "User")}
      closeForm={() => onClose(false)}
      handleFormSubmit={formik.handleSubmit}
      idToEdit={idToEdit}
    >
      <Components.QIPhotoUploader
        label={getLocalizedString("upload_image", "Upload Image")}
        src={formik.values.image}
        onChange={(files) => formik.setFieldValue("image", files?.length > 0 ? files[0] : null)}
        showJustImage
        base64={false}
        initalImageUrl={formik.values.image}
        placeHolderImg={userPlaceholder}
        photoGuide={`
        <h4 className="font-semibold">${getLocalizedString(
          "image_upload_guide",
          "Image Upload guide"
        )} - </h4>
        <div className="ml-2">
          <p>${getLocalizedString("max_size", "Max size")} 100kb</p>
          <p>${getLocalizedString("file_format", "File format")} .jpg/ .jpeg/ .png/ .gif</p>
          <p>${getLocalizedString(
            "preferred_image_aspect_ratio",
            "Preferred image aspect ratio"
          )} 1:1</p>
        </div>
      `}
        error={formik.touched.image && formik.errors.image}
      />
      <Components.QIInput
        label={getLocalizedString("first_name", "First Name")}
        {...formik.getFieldProps("first_name")}
        error={formik.touched.first_name && formik.errors.first_name}
      />
      <Components.QIInput
        label={getLocalizedString("last_name", "Last Name")}
        {...formik.getFieldProps("last_name")}
        error={formik.touched.last_name && formik.errors.last_name}
      />
      <Components.QIInput
        label={getLocalizedString("rfid", "ID/RFID")}
        {...formik.getFieldProps("external_driver_rfid")}
        error={formik.touched.external_driver_rfid && formik.errors.external_driver_rfid}
      />
      <div className="flex justify-between country-code-and-number">
        <Components.QICustomSelect
          label={getLocalizedString("country_code", "Country Code")}
          className="country-code-and-number_item1"
          {...formik.getFieldProps("country_code")}
          onChange={(value) => formik.setFieldValue("country_code", value)}
          onSearch={setCountryCodeSearchKey}
          error={formik.touched.country_code && formik.errors.country_code}
          labelClassName="country-code"
        >
          <li value="">{getLocalizedString("select", "Select")}</li>
          {countryCode?.map((code, index) => (
            <li key={index} value={code.dial_code} title={`${code.dial_code} (${code.name})`}>
              {code.dial_code} ({code.name})
            </li>
          ))}
        </Components.QICustomSelect>

        <Components.QIInput
          label={getLocalizedString("phone_no", "Phone No.")}
          placeholder={getLocalizedString("phone_no", "Phone No.")}
          className="country-code-and-number_item2"
          customLabelClass="phone-number"
          customWrapperClass="phone-number-wrapper"
          {...formik.getFieldProps("phone_no")}
          error={formik.touched.phone_no && formik.errors.phone_no}
        />
      </div>
      <Components.QIInput
        label={getLocalizedString("email", "Email")}
        {...formik.getFieldProps("email")}
        error={formik.touched.email && formik.errors.email}
        disabled={idToEdit}
      />
      <Components.QICustomSelect
        label={getLocalizedString("preferred_language", "Preferred Language")}
        {...formik.getFieldProps("locale_id")}
        onChange={(value) => {
          formik.setFieldValue("locale_id", value);
          setLanguagesSearch("");
        }}
        error={formik.touched.locale_id && formik.errors.locale_id}
        labelClassName="preferred-language"
        onSearch={setLanguagesSearch}
      >
        <>
          <li value={1}>English</li>
          {languages?.data?.map((lan, index) => (
            <li value={lan.id} key={index}>
              {lan.name}
            </li>
          ))}
        </>
      </Components.QICustomSelect>

      <Components.QICustomSelect
        label={getLocalizedString("preferred_layout", "Preferred Layout")}
        {...formik.getFieldProps("layout_id")}
        onChange={(value) => {
          formik.setFieldValue("layout_id", value);
        }}
        error={formik.touched.layout_id && formik.errors.layout_id}
        labelClassName="preferred-layout"
      >
        <>
          {layouts?.data?.map((layout, index) => (
            <li value={layout.id} key={index}>
              {layout.name}
            </li>
          ))}
        </>
      </Components.QICustomSelect>

      {/*
      <Components.QIInput
        label="Driving Licence"
        {...formik.getFieldProps("driving_licence")}
        error={formik.touched.driving_licence && formik.errors.driving_licence}
        disabled={true}
      />
      <Components.QIInput
        label="Driver Licence Validity"
        {...formik.getFieldProps("driving_licence_validity")}
        error={formik.touched.driving_licence_validity && formik.errors.driving_licence_validity}
        disabled={true}
      />
      <Components.QIInput
        label="Driver Description"
        textarea
        {...formik.getFieldProps("driver_description")}
        error={formik.touched.driver_description && formik.errors.driver_description}
        disabled={true}
      /> */}
      <Components.QISwitch
        className="active-status"
        label={getLocalizedString("active", "Active")}
        value={formik.values.is_enabled}
        readOnly={JSON.parse(localStorage.getItem("current-user"))?.email == formik?.values?.email}
        disabled={JSON.parse(localStorage.getItem("current-user"))?.email == formik?.values?.email}
        onChange={() => {
          formik.setFieldValue("is_enabled", !formik.values.is_enabled);
        }}
        error={formik.touched.is_enabled && formik.errors.is_enabled}
      />
      {!idToEdit && (
        <Components.QIInput
          label={getLocalizedString("password", "Password")}
          {...formik.getFieldProps("password")}
          error={formik.touched.password && formik.errors.password}
        />
      )}
      <div className={`qi-input ${formik.touched.roles && formik.errors.roles && "error"}`}>
        <label className="qi-input_label">
          {getLocalizedString("applications", "Applications")}
        </label>
        <div className="roles-wrapper">
          <ul className="roles-wrapper_list">
            {isClientAppsSuccess &&
              clientApps?.data?.map((clientApp, index) => (
                <li key={index} className={clientApp?.name}>
                  <h4 className="title">
                    <strong>{clientApp.name}</strong>
                  </h4>
                  <ul className="roles_and_applications">
                    {rolesList?.data?.map((role) => (
                      <Components.QICheckBox
                        label={role.name}
                        id={`${clientApp.id}_${role.id}`} // unique DOM id
                        checked={formik.values.roles?.some(
                          ([cAppId, rId]) => cAppId === clientApp.id && rId === role.id
                        )}
                        onChange={() => handleRoleSelection(role.id, clientApp.id)}
                        className={`${clientApp.id}_${role.id}`} // optional unique class
                        key={`${clientApp.id}-${role.id}`} // unique react key
                      />
                    ))}
                  </ul>
                </li>
              ))}
          </ul>
          {formik.touched.roles && formik.errors.roles && (
            <div className="qi-input_error">
              <SvgIcon name="info" />
              <p className="qi-input_error_message ">{formik.errors.roles}</p>
            </div>
          )}
        </div>
      </div>
      <Components.QIMultiSelectDropDown
        label={getLocalizedString("group", "Group")}
        selected={formik.values.user_group_ids}
        onChange={(value) => formik.setFieldValue("user_group_ids", value)}
        data={userGroups?.data || []}
        className="narrow-multiselect-dropdown"
        readOnly={formik.values.roles.length > 0 ? false : true}
        isDeletable={isUserGroupDeletable}
        actionError={handleUserGroupSelection}
      />
    </FormContainer>
  );
};
